import type { FestiveEventDto } from "@hrms/types";

/**
 * One festive event as the date rules and the components use it. These come from the API (the
 * admin's Festive Events section), so nothing here is hard-coded.
 */
export interface Festival {
  /** The preset key for built-in festivals (used by the ?festival= preview), else the event id. */
  id: string;
  name: string;
  /** The greeting, e.g. "Happy Diwali". Shown as "Happy Diwali!" on the day itself. */
  wish: string;
  /** Hex colour, from the admin's pick. The light theme uses inkOnLight(accent). */
  accent: string;
  rest: string;
  windowDays: number;
  /** Which icon to draw: a festival icon (diwali, xmas, ...) or a company icon (trophy, cake, ...). */
  icon: string;
  /** Same date every year. */
  fixed?: { month: number; day: number };
  /** One specific date, keyed by its year. */
  dates?: Record<number, string>;
}

/**
 * Maps an API event to the shape the date rules read. A yearly event gets `fixed`, a one-time
 * event gets `dates`. An event with no date yet gets neither, so it never shows.
 */
export function toFestival(event: FestiveEventDto): Festival {
  const base = {
    id: event.presetKey ?? event.id,
    name: event.name,
    wish: event.greeting,
    accent: event.accent,
    rest: event.message,
    windowDays: event.windowDays,
    icon: event.icon,
  };
  if (event.recurrence === "ANNUAL" && event.month !== null && event.day !== null) {
    return { ...base, fixed: { month: event.month, day: event.day } };
  }
  if (event.recurrence === "ONE_TIME" && event.eventDate) {
    return { ...base, dates: { [Number(event.eventDate.slice(0, 4))]: event.eventDate } };
  }
  return base;
}
